import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RegisterForm from '../../components/forms/RegisterForm';
import AuthShell from '../../components/onboarding/AuthShell';
import VerifyStep from '../../components/onboarding/VerifyStep';
import Toast from '../../components/Toast';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/authService';
import { PENDING_VERIFICATION_KEY } from '../../utils/onboarding';

const readPendingEmail = () => {
  try {
    return localStorage.getItem(PENDING_VERIFICATION_KEY);
  } catch {
    return null;
  }
};

const writePendingEmail = (email) => {
  try {
    if (email) localStorage.setItem(PENDING_VERIFICATION_KEY, email);
    else localStorage.removeItem(PENDING_VERIFICATION_KEY);
  } catch {
    // storage unavailable: the flow still works, it just can't resume after a refresh
  }
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [pendingEmail, setPendingEmail] = useState(readPendingEmail);
  const [toast, setToast] = useState(null);

  const handleRegister = async (form) => {
    const result = await authService.register(form);
    if (!result.success) {
      const field = /company/i.test(result.error) ? 'companyName' : /email|account with/i.test(result.error) ? 'email' : /password/i.test(result.error) ? 'password' : null;
      if (field) return { [field]: result.error };
      setToast({ message: result.error || 'Registration failed. Please try again.', type: 'error' });
      return null;
    }
    writePendingEmail(form.email);
    setPendingEmail(form.email);
    return null;
  };

  const handleJoinRequest = async (details) => {
    const result = await authService.joinRequest(details);
    if (!result.success) setToast({ message: result.error, type: 'error' });
    return result.success;
  };

  const handleVerify = async (code) => {
    const result = await authService.verifyCode(pendingEmail, code);
    if (!result.success) return result.error;
    writePendingEmail(null);
    await login(result.token);
    navigate('/onboarding', { replace: true });
    return null;
  };

  const handleResend = async () => {
    const result = await authService.resendVerification(pendingEmail);
    if (result.success) setToast({ message: 'A new code is on its way.', type: 'success' });
    return result.success ? null : result.error;
  };

  const changeEmail = () => {
    writePendingEmail(null);
    setPendingEmail(null);
  };

  return (
    <>
      {pendingEmail ? (
        <AuthShell step="verify" title="Check your email" subtitle={`We sent a 6-digit code to ${pendingEmail}.`}>
          <VerifyStep email={pendingEmail} onVerify={handleVerify} onResend={handleResend} onChangeEmail={changeEmail} />
        </AuthShell>
      ) : (
        <AuthShell step="account" title="Create your account" subtitle="14-day free trial. No card needed.">
          <RegisterForm onRegister={handleRegister} onJoinRequest={handleJoinRequest} />
        </AuthShell>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}
